import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import styles from "./ClientApproverSetting.module.css";
import { Approver, ClientApproverSettingProps } from "./ClientApproverSetting.utils";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import Portal from '../Portal/Portal'

/**
 * ClientApproverSetting
 *
 * Displays the UI to select and manage approvers from a user list.
 * - Supports adding approvers via a dropdown.
 * - Prevents duplicates.
 * - Renders approver cards with basic info and removal option.
 */
const ClientApproverSetting: React.FC<ClientApproverSettingProps> = ({
  allUsers,
  selectedApprovers,
  approvalCheck,
  setSelectedApprovers,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties | null>(null);
  const [searchText, setSearchText] = useState("");
  const addBtnRef = useRef<HTMLButtonElement>(null);

  const availableUsers = allUsers.filter(
    (user) => !selectedApprovers.some((a) => a.UserName === user.UserName && a.Active !== "N") && user.ShowInUI === "Y"
  );

  const filteredUsers = availableUsers.filter((user) =>
    `${user.FirstName} ${user.LastName} ${user.Email} ${user.Role}`
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  const handleAddApprover = (user: Approver) => {
    const exists = selectedApprovers.find((a) => a.UserName === user.UserName);
    const updated = exists
      ? selectedApprovers.map((a) =>
          a.UserName === user.UserName ? { ...a, Active: "Y" } : a
        )
      : [...selectedApprovers, { ...user, Active: "Y" }];
    setSelectedApprovers(updateApproverOrder(updated));
    setShowDropdown(false);
    setSearchText("");
  };

  const updateApproverOrder = (approvers: Approver[]): Approver[] => {
    let activeOrder = 1;
    return approvers.map((approver) => {
      if (approver.Active === "Y") {
        return { ...approver, ApproverOrder: activeOrder++ };
      }
      return { ...approver, ApproverOrder: 0 };
    });
  };

  const openDropdown = () => {
    if (addBtnRef.current) {
      const rect = addBtnRef.current.getBoundingClientRect();
      setDropdownStyles({
        position: "fixed",
        top: rect.bottom + 5,
        left: rect.left,
        width: 300,
        zIndex: 9999,
        background: "#fff",
        borderRadius: 5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        maxHeight: 300,
        overflowY: "auto",
      });
      setShowDropdown(true);
    }
  };

  const closeDropdown = () => setShowDropdown(false);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const dropdownEl = document.getElementById("approver-custom-dropdown");
      if (
        showDropdown &&
        !dropdownEl?.contains(e.target as Node) &&
        !addBtnRef.current?.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showDropdown]);

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const activeApprovers = selectedApprovers.filter((a) => a.Active === "Y");
    const inactiveApprovers = selectedApprovers.filter((a) => a.Active !== "Y");

    const items = Array.from(activeApprovers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSelectedApprovers(updateApproverOrder([...items, ...inactiveApprovers]));
  };

  return (
    <>
      <div className={styles.approverHeaderRow}>
        <div className={styles.headerTitle}>Approvers</div>
        <div>
          <Button
            className={`${styles.addBtn} ${approvalCheck ? styles.disabled : ""}`}
            ref={addBtnRef}
            onClick={openDropdown}
            disabled={filteredUsers.length === 0}
          >
            <Icon.Plus
              className={`${styles.addBtnIcon} ${approvalCheck ? styles.disabled : ""}`}
            />{" "}
            Add approver
          </Button>
        </div>
      </div>

      {showDropdown && dropdownStyles &&
        ReactDOM.createPortal(
          <div id="approver-custom-dropdown" style={dropdownStyles}>
            <div className={styles.dropdownSearchContainer}>
              <Icon.Search className={styles.searchIcon} />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search approver..."
                className={styles.dropdownSearch}
                defaultValue="Hello!"
                autoFocus
              />
            </div>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <button
                  key={user.UserName}
                  onClick={() => handleAddApprover(user)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 12px",
                    border: "none",
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    fontSize: 12,
                    backgroundColor: "white",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                >
                  {user.UserFullName} - {user.Role}
                </button>
              ))
            ) : (
              <div style={{ padding: "8px 12px", color: "#777" }}>No approvers available</div>
            )}
          </div>,
          document.body
      )}

      {selectedApprovers.filter((a) => a.Active === "Y" || a.Active === true).length === 0 && (
        <p className="text-danger">No approvers present in the document.</p>
      )}

      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="approvers">
          {(provided) => (
            <div className={styles.approverList} {...provided.droppableProps} ref={provided.innerRef}>
              {selectedApprovers
                .filter((a) => a.Active === "Y")
                .map((approver, index) => (
                  <Draggable
                    key={approver.UserName}
                    draggableId={approver.UserName}
                    index={index}
                    isDragDisabled={approvalCheck}
                  >
                    {(provided, snapshot) => {
                      const card = (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`${styles.approverCard} ${
                            snapshot.isDragging ? styles.dragging : ""
                          }`}
                        >
                          <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={<Tooltip id="button-tooltip">Drag to reorder</Tooltip>}
                          >
                            <Icon.GripVertical
                              size={18}
                              className={`${styles.dragHandle} ${approvalCheck ? styles.disabled : ""}`}
                            />
                          </OverlayTrigger>

                          <div className={styles.avatarCircle}>
                            {`${approver.FirstName?.[0] ?? ""}${approver.LastName?.[0] ?? ""}`.toUpperCase()}
                          </div>
                          <div className={styles.approverDetails}>
                            <div className={styles.approverName}>
                              {approver.UserFullName}
                            </div>
                            <div className={styles.meta}>
                              {approver.UserRole ?? approver.Role}{" "}
                              {approver.FirmName ? " - " : ""} {approver.FirmName}
                            </div>
                            <div className={styles.emailHolder}>
                              <a href={`mailto:${approver.UserName}`} className={styles.email}>
                                {approver.UserName}
                              </a>
                            </div>
                          </div>

                          {(() => {
                            const activeApprovers = selectedApprovers.filter((a) => a.Active === "Y");
                            const isLastActive =
                              activeApprovers.length === 1 &&
                              activeApprovers[0].UserName === approver.UserName;
                            const isDeleteDisabled =
                              (!approvalCheck && isLastActive) || approvalCheck;

                            return (
                              <Icon.Trash
                                className={`${styles.deleteIcon} ${
                                  isDeleteDisabled ? styles.disabled : ""
                                }`}
                                onClick={() => {
                                  if (isDeleteDisabled) return;
                                  setSelectedApprovers(
                                    updateApproverOrder(
                                      selectedApprovers.map((a) =>
                                        a.UserName === approver.UserName
                                          ? { ...a, Active: "N" }
                                          : a
                                      )
                                    )
                                  );
                                }}
                              />
                            );
                          })()}
                        </div>
                      );

                      return snapshot.isDragging ? <Portal>{card}</Portal> : card;
                    }}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default ClientApproverSetting;
